import React from "react";
import { EventsCardSection } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import Link from "next/link";

interface EventsCardSectionProps {
  blok?: EventsCardSection & SbBlokData;
}

const EventsCardSectionComponent: React.FC<EventsCardSectionProps> = ({ blok }) => {
  if (!blok) return null;
  const { events } = blok;

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'long' });
    return { day, month, date };
  };

  const formatEventTime = (startDate: string, endDate?: string) => {
    const start = new Date(startDate);
    const startTime = start.toLocaleString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    if (endDate) {
      const end = new Date(endDate);
      const endTime = end.toLocaleString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return `${startTime} – ${endTime}`;
    }

    return startTime;
  };

  const today = new Date();
  interface EventContent {
    body: Array<{
      date: string;
    }>;
  }

  const upcomingEvents = events?.filter(event => new Date((event.content as EventContent).body[0].date) > today) || [];
  const previousEvents = events?.filter(event => new Date((event.content as EventContent).body[0].date) <= today) || [];

  const renderEventCard = (event: any, isUpcoming: boolean) => {
    const { day, month } = formatEventDate(event.content.body[0].date);
    const eventSlug = event.slug;
    const eventTitle = event.content.body[0].title;
    const eventDesc = event.content.body[0].subtitle;
    const startDate = event.content.body[1].date;
    const endDate = event.content.body[1].date_end;
    const location = event.content.body[1].address || 'Online';
    const eventType = event.content.body[1].type || 'webinar';
    const thumbnailImage = event.content.metatags?.twitter_image || '/assets/events/event-webinar.png';

    return (
      <div className="col" key={event.uuid}>
        <article
          className={`event-preview ${!isUpcoming ? `event-${eventType}` : ''} d-flex`}
          data-eventdate={event.content.body[0].date}
        >
          <Link
            href={`/events/${eventSlug}`}
            className="d-flex flex-column h-100"
          >
            <div
              className="event-preview-image js-lazy-load"
              data-background-image={thumbnailImage}
              style={{
                backgroundImage: `url("${thumbnailImage}")`
              }}
            >
              <div className="event-calender-date">
                <div className="event-type"></div>
                <div className="event-date">
                  <span>{day}</span>{month}
                </div>
              </div>
            </div>
            <div className="event-preview-content mb-0">
              <h3>{eventTitle}</h3>
              {eventDesc && (
                <p className="event-preview-desc">{eventDesc}</p>
              )}
            </div>
            <div className="mt-auto ps-3">
              <button className="btn btn-primary">Find out more</button>
            </div>
            <div className="event-preview-footer align-items-center">
              <ul className="list-unstyled mb-0">
                <li>
                  <i className="far fa-clock" aria-hidden="true"></i>
                  {formatEventTime(startDate, endDate)}
                </li>
                <li>
                  <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                  {location}
                </li>
              </ul>
            </div>
          </Link>
        </article>
      </div>
    );
  };

  return (
    <>
      {upcomingEvents.length > 0 && (
        <div className="bg-light">
          <div className="container py-4 py-md-5">
            <h2 className="text-center mb-5">Upcoming events</h2>
            <div className="row justify-content-center row-cols-1 row-cols-md-3 gy-4 gx-md-4">
              {upcomingEvents.map(event => renderEventCard(event, true))}
            </div>
          </div>
        </div>
      )}

      {previousEvents.length > 0 && (
        <div {...storyblokEditable(blok)} className="previous-events container py-4 py-md-5">
          <h2 className="text-center mb-5">Previous events</h2>
          <div className="row row-cols-1 row-cols-md-3 gy-4 gx-md-4">
            {previousEvents.map(event => renderEventCard(event, false))}
          </div>
        </div>
      )}
    </>
  );
};

export default EventsCardSectionComponent;